/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-key */
import {
  Pagination,
  PaginationContainer,
  PaginationNext,
  PaginationPage,
  PaginationPageGroup,
  PaginationPrevious,
  usePagination as ajnaUsePagination
} from '@ajna/pagination';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
  Cell,
  Column,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table';

import { DefaultFilterForColumn } from './utils/Filters';

type TableProps = {
  title?: string;
  itemsCount?: number;
  columns: Column<object>[];
  data: object[];
  details?: {
    columns: string[];
    values: object[];
  };
  modalTitle?: string;
  sort?: string;
  withPagination?: boolean;
  activePageIndex?: number;
  onPageChange?: (activePageIndex: number) => void;
  isLoadingData?: boolean;
  isLoadingCountPagination?: boolean;
};

export const InteractiveTable = ({
  columns,
  data,
  sort,
  itemsCount,
  withPagination = false,
  onPageChange,
  activePageIndex = 1,
  isLoadingData,
  isLoadingCountPagination
}: TableProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    pageCount,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable<object>(
    {
      columns,
      data,
      initialState: {
        pageIndex: activePageIndex,
        sortBy: [
          {
            id: sort ? sort : '',
            desc: false
          }
        ]
      },
      defaultColumns: { Filer: DefaultFilterForColumn },
      manualPagination: withPagination ? true : undefined,
      pageCount: itemsCount ? Math.ceil(itemsCount / 15) : 1
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  console.log('activePageIndex on interactive table: ', activePageIndex);
  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPage);
    }
  }, [currentPage]);

  return (
    <>
      <TableContainer maxH="30rem" overflowY="auto">
        {isLoadingData ? (
          <Flex w="100%" justify="center" h="30rem">
            <Flex alignItems="center" justify="center" direction="column">
              <Spinner />
              <Text marginTop={5}>Estamos carregando os dados...</Text>
            </Flex>
          </Flex>
        ) : (
          <Table
            {...getTableProps()}
            size="sm"
            colorScheme="whiteAlpha"
            position="relative"
          >
            <Thead
              h="3.25rem"
              zIndex={10}
              position="sticky"
              top={0}
              transform="translateX(-4px)"
            >
              {headerGroups.map((headerGroup) => (
                <Tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <Th
                      whiteSpace="normal"
                      background="#fff"
                      color="#1C375C"
                      fontSize=".8rem"
                      textTransform="none"
                      {...column.getHeaderProps(
                        column.getSortByToggleProps({ title: 'Ordenar' })
                      )}
                    >
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <TriangleDownIcon ml="0.5rem" />
                          ) : (
                            <TriangleUpIcon ml="0.5rem" />
                          )
                        ) : (
                          <></>
                        )}
                      </span>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>

            <Tbody {...getTableBodyProps()}>
              {rows.slice(0, itemsCount).map((row: any) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    transition="all .4s ease-in-out"
                    h="3rem"
                    bg="#F3F3F3"
                    borderLeft=".4rem solid #299EAD"
                    borderBottom=".5rem solid #fff"
                  >
                    {row.cells.map((cell: Cell<object, any>) => {
                      return (
                        <Td {...cell.getCellProps()}>{cell.render('Cell')}</Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </TableContainer>
      {withPagination && (
        <PaginationComponent
          pageCount={pageCount}
          pageIndex={pageIndex}
          onPageChange={setCurrentPage}
          itemsCount={itemsCount}
          nextPage={nextPage}
          previousPage={previousPage}
          loading={isLoadingCountPagination}
          disabled={isLoadingData}
        />
      )}
    </>
  );
};

type PaginationProps = {
  pageCount?: number;
  itemsCount?: number;
  pageIndex?: number;
  loading?: boolean;
  disabled?: boolean;
  onPageChange?: (page: number) => void;
  nextPage?: () => void;
  previousPage?: () => void;
  gotoPage?: (pageNumber: number) => void;
};
const PaginationComponent = ({
  pageCount = 0,
  pageIndex = 1,
  itemsCount,
  loading = false,
  disabled = false,
  gotoPage,
  previousPage,
  nextPage,
  onPageChange
}: PaginationProps) => {
  /* const visibleNumberPages = useMemo(() => {
    let numberPages: number[] = [];
    console.log({ pageIndex, pageCount });
    if (pageIndex < 4) {
      numberPages = [0, 1, 2, 3, 4];
    } else if (pageIndex >= 4 && pageIndex < pageCount - 1) {
      numberPages = [pageIndex - 1, pageIndex, pageIndex + 1];
    }

    if (pageIndex >= pageCount - 2) {
    }
    return numberPages;
    return new Array(pageCount).fill(null).map((e, i) => i);
  }, [pageIndex, pageCount]); */

  const { currentPage, setCurrentPage, pagesCount, pages } = ajnaUsePagination({
    pagesCount: pageCount,
    initialState: { currentPage: 1 },
    limits: {
      inner: 2,
      outer: 1
    }
  });

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPage);
    }
  }, [currentPage]);

  if (loading) {
    return (
      <HStack justifyContent="flex-end" paddingY={5}>
        <Spinner size="xs" />
      </HStack>
    );
  }
  return (
    <HStack
      justifyContent="flex-end"
      paddingY={5}
      pointerEvents={!loading || !disabled ? 'all' : 'none'}
    >
      <Box>
        <Text fontSize="0.8rem">
          Exibindo de {pageIndex === 1 ? 1 : (pageIndex - 1) * 15} até{' '}
          {pageIndex === pageCount ? itemsCount : pageIndex * 15} de{' '}
          {itemsCount}
        </Text>
      </Box>
      <Pagination
        pagesCount={pagesCount}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      >
        <PaginationContainer>
          <PaginationPrevious variant="ghost">
            <ChevronLeftIcon />
          </PaginationPrevious>
          <PaginationPageGroup mx="2">
            {pages.map((page: number) => (
              <PaginationPage
                variant="ghost"
                _current={{
                  bg: '#FA6B25',
                  color: '#FFF',
                  _hover: { bg: '#FA6B25' }
                }}
                outline="none"
                boxShadow="none !important"
                p="4"
                key={`pagination_page_${page}`}
                page={page}
              />
            ))}
          </PaginationPageGroup>
          <PaginationNext variant="ghost">
            <ChevronRightIcon />
          </PaginationNext>
        </PaginationContainer>
      </Pagination>
      {/* <Flex columnGap={2}>
        <IconButton
          icon={<ChevronLeftIcon />}
          variant="ghost"
          aria-label=""
          onClick={previousPage}
        />
        {visibleNumberPages.map((pageNumber) => {
          const active = pageIndex === pageNumber || false;
          return (
            <IconButton
              key={pageNumber}
              icon={<span>{pageNumber + 1}</span>}
              variant="ghost"
              fontWeight="semibold"
              color={active ? 'white' : undefined}
              backgroundColor={active ? '#FA6B25' : undefined}
              _hover={active ? { bg: '#FA6B25' } : undefined}
              _active={
                active
                  ? {
                      bg: '#de5e1f'
                    }
                  : undefined
              }
              outline="none"
              boxShadow="none !important"
              aria-label=""
              onClick={() => gotoPage(pageNumber)}
            />
          );
        })}
        <IconButton
          icon={<ChevronRightIcon />}
          variant="ghost"
          aria-label=""
          onClick={nextPage}
        />
      </Flex> */}
    </HStack>
  );
};
